import { FORM_ERROR } from 'final-form';
import i18next from 'i18next';
import queryString from 'query-string';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { baseApi } from '~src/services/ApiController';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { showNotification } from '~src/store/app/actions';
import { NotificationI } from '~src/store/app/interfaces';
import { loginUser, setRedirect, signupCompany } from '~src/store/session/actions';
import { LoginUserCredentials, SignupFormValuesI } from '~src/store/session/interfaces';
import { trimValues } from '~src/utils';
import { setup } from '../../../config/server/server.config';

import Accounts from './components/Accounts';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import {
  FormContainer,
  InfoContent,
  InfoGrid,
  InfoHeader,
  LoginContainer,
  LoginGrid,
  LoginTitle,
  LoginWrapper,
  MoreInfoTxt,
  SignUpGrid,
  SignupInfoContainer,
} from './login.sc';

const ROUTE_SIGNUP = '/signup';

interface StateProps {
  hasMultipleAccounts: boolean;
  loginLoading: boolean;
  multiLoginLoading: boolean;
  signUpLoading: boolean;
}

interface DispatchProps {
  loginUser(credentials: LoginUserCredentials): Promise<void>;

  signupCompany(companyData: SignupFormValuesI): ApiReduxResp;

  setRedirect(redirect?: string): void;

  showNotification(notification: NotificationI): void;
}

interface DefaultProps {
  hasMultipleAccounts: boolean;
  loginLoading: boolean;
  multiLoginLoading: boolean;
  signUpLoading: boolean;
}

type Props = StateProps & DispatchProps & WithTranslation & RouteComponentProps<any>;

interface State {
  signupSuccess: boolean;
  okrs: boolean;
  surveys: boolean;
}

class Login extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    hasMultipleAccounts: false,
    loginLoading: false,
    multiLoginLoading: false,
    signUpLoading: false,
  };

  state = {
    signupSuccess: false,
    okrs: false,
    surveys: false,
  };

  componentDidMount() {
    // request for checking api version in header 'x-api-version'
    baseApi.get('/helo');
    const { location, okrs, surveys } = queryString.parse(this.props.location.search) as {
      location?: string;
      okrs?: string;
      surveys?: string;
    };
    this.state.okrs = okrs === '1';
    this.state.surveys = surveys === '1';
    this.props.setRedirect(location);
  }

  onSignupSubmit = (values: SignupFormValuesI) => {
    interface DatasetI extends SignupFormValuesI {
      dataset?: string;
    }

    const updatedValues: DatasetI = { ...values, okrs: this.state.okrs, surveys: this.state.surveys };
    const { dataset } = queryString.parse(this.props.location.search) as { dataset?: string };
    if (dataset) {
      updatedValues.dataset = dataset;
    }

    return this.props.signupCompany(updatedValues).then((rsp) => {
      if (rsp.error) {
        const errors = rsp.error.response.data;
        if (errors.companyName) {
          this.props.showNotification({
            id: 'signup_error_company',
            txtKey: 'x.error.signup.email_company_combination_unique',
            type: 'error',
          });
        }
        if (errors.email) {
          this.props.showNotification({
            id: 'invalid_email',
            txtKey: 'x.error.signup.invalid_email',
            type: 'error',
          });
        }
        return;
      }
      const redirect = updatedValues.okrs && !updatedValues.surveys ? 'okrsOnly' : 'default';
      document.location.href =
        (setup.signup && setup.signup.redirects[redirect][i18next.language as 'en' | 'de']) ||
        setup.signup.redirects[redirect].en;
    });
  };

  handleLogin = (values: LoginUserCredentials) => {
    return this.props.loginUser(trimValues(values)).catch((error) => {
      logger.info('Error on handle Login', {
        error,
        info: {
          componentStack: 'Login.tsx',
        },
      });

      if (error.response && error.response.data.error === 'auth.failed') {
        return {
          [FORM_ERROR]: 'login_failed',
        };
      }
    });
  };

  render() {
    const { t, location, loginLoading } = this.props;
    const isShowSignup =
      location.pathname === ROUTE_SIGNUP || (this.state.signupSuccess && location.pathname !== routes.login);
    const signupContainerClassName =
      location.pathname === ROUTE_SIGNUP && !this.state.signupSuccess ? 'visible' : '';
    return (
      <section>
        {this.props.multiLoginLoading ? (
          <LoadingSpinner size={18} alignCls="page-cover" color="white" />
        ) : (
          <FormContainer>
            <SignupInfoContainer className={signupContainerClassName}>
              <InfoGrid>
                <InfoHeader data-translation="x.signup.information.text.title">
                  {t('x.signup.information.text.title')}
                </InfoHeader>
                <InfoContent data-translation="x.signup.information.text.subtitle">
                  {t('x.signup.information.text.subtitle')}
                </InfoContent>
                <MoreInfoTxt
                  dangerouslySetInnerHTML={{
                    __html: t('x.signup.information.brochure_link_text', {
                      href: t('x.signup.information.brochure_url'),
                    }),
                  }}
                />
              </InfoGrid>
            </SignupInfoContainer>
            <LoginContainer $full={location.pathname !== ROUTE_SIGNUP || this.state.signupSuccess}>
              {isShowSignup ? (
                this.state.okrs || this.state.surveys ? (
                  <SignUpGrid>
                    <SignupForm
                      t={t}
                      signupLoading={this.props.signUpLoading}
                      onSubmit={this.onSignupSubmit}
                      showSignupSuccessMessage={this.state.signupSuccess}
                    />
                  </SignUpGrid>
                ) : (
                  <MoreInfoTxt dangerouslySetInnerHTML={{ __html: t('x.signup.no_sections_activated') }} />
                )
              ) : (
                <LoginGrid>
                  <LoginWrapper>
                    <LoginTitle
                      data-translation={[
                        'x.login.form.multiple_accounts.header.title',
                        'x.login.form.header.title',
                      ]}
                    >
                      {this.props.hasMultipleAccounts
                        ? t('x.login.form.multiple_accounts.header.title')
                        : t('x.login.form.header.title')}
                    </LoginTitle>
                    {this.props.hasMultipleAccounts ? (
                      <Accounts />
                    ) : (
                      <LoginForm t={t} loginLoading={loginLoading} onSubmit={this.handleLogin} />
                    )}
                  </LoginWrapper>
                </LoginGrid>
              )}
            </LoginContainer>
          </FormContainer>
        )}
      </section>
    );
  }
}

const mapState2Props = ({ user: { session } }: StoreI): StateProps => ({
  hasMultipleAccounts: session.accounts.hasMultipleAccounts,
  loginLoading: session.loginLoading,
  multiLoginLoading: session.multiLoginLoading,
  signUpLoading: session.signUpLoading,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  loginUser: (credentials) => dispatch(loginUser(credentials)),
  signupCompany: (companyData) => dispatch(signupCompany(companyData)),
  setRedirect: (redirect) => dispatch(setRedirect(redirect)),
  showNotification: (notification) => dispatch(showNotification(notification)),
});

const LoginT = withTranslation()(Login);
const LoginC = connect(mapState2Props, mapDispatch2Props)(LoginT);

export default LoginC;
